import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CalendarDays, XCircle, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import Modal from '../UI/Modal';
import { CountBadge } from '../UI/CountBadge';
import { TradeBadge } from '../UI/TradeBadge';
import {
  getDepartmentColor,
  getDepartmentIcon,
  getDepartmentShortName,
} from '../../config/departmentConfig';
import { isTradeRedundantWithDepartment } from '../../config/tradeConfig';
import { formatDate } from '../../utils/dateUtils';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';
import { getLeaveProjectFields } from '../../utils/leaveDisplayUtils';
import LeavePeriodDisplay from './LeavePeriodDisplay';

/**
 * Date detail modal for leave calendars with department grouping, summary pills,
 * and collapsible sections. Used by both LeaveCalendar and LeaveManagement CalendarTab.
 *
 * @param {Object} props
 * @param {boolean} props.show
 * @param {Function} props.onClose
 * @param {Date} props.date - Selected calendar date
 * @param {Array} props.applications - Leave applications for the selected date
 * @param {string} [props.userDepartment] - Current user's department name (prioritised in sort/expand)
 * @param {Function} [props.renderBadge] - (app) => JSX for right-aligned badge per row
 * @param {Function} [props.renderLeaveTypeInline] - (app) => JSX for inline leave type in row 2
 * @param {boolean} [props.isAdmin=false] - Use admin (red) or employee (blue) theme
 */
const GroupedDateLeaveModal = ({
  show,
  onClose,
  date,
  applications,
  userDepartment,
  renderBadge,
  renderLeaveTypeInline,
  isAdmin = false,
}) => {
  const theme = isAdmin
    ? { icon: 'text-red-600', pillActive: 'bg-red-600 text-white border-red-600', pillInactive: 'text-gray-500 border-transparent hover:text-gray-700', badgeActive: 'bg-white text-red-600', badgeInactive: 'bg-red-100 text-red-600' }
    : { icon: 'text-blue-800', pillActive: 'bg-blue-800 text-white border-blue-800', pillInactive: 'text-gray-500 border-transparent hover:text-gray-700', badgeActive: 'bg-white text-blue-800', badgeInactive: 'bg-blue-100 text-blue-800' };
  const [collapsedDepts, setCollapsedDepts] = useState(new Set());

  const groupedDepartments = useMemo(() => {
    if (!applications?.length) return [];

    const groups = {};
    applications.forEach(app => {
      const dept = app.department_name || app.department || 'Other';
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(app);
    });

    return Object.keys(groups)
      .sort((a, b) => {
        if (a === userDepartment) return -1;
        if (b === userDepartment) return 1;
        if (a === 'Other') return 1;
        if (b === 'Other') return -1;
        return a.localeCompare(b);
      })
      .map(dept => ({ department: dept, applications: groups[dept] }));
  }, [applications, userDepartment]);

  // Smart collapse: <=3 departments all expanded; >3 collapses non-user departments
  useEffect(() => {
    if (groupedDepartments.length <= 3) {
      setCollapsedDepts(new Set());
    } else {
      setCollapsedDepts(new Set(
        groupedDepartments
          .map(g => g.department)
          .filter(d => d !== userDepartment)
      ));
    }
  }, [groupedDepartments, userDepartment]);

  const toggleDept = useCallback((dept) => {
    setCollapsedDepts(prev => {
      const next = new Set(prev);
      if (next.has(dept)) next.delete(dept);
      else next.add(dept);
      return next;
    });
  }, []);

  const handlePillClick = useCallback((department) => {
    const isCurrentlyCollapsed = collapsedDepts.has(department);
    toggleDept(department);
    if (isCurrentlyCollapsed) {
      setTimeout(() => {
        const el = document.getElementById(`dept-section-${department.replace(/\s+/g, '-')}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [collapsedDepts, toggleDept]);

  const expandAll = useCallback(() => {
    setCollapsedDepts(new Set());
  }, []);

  if (!show || !date) return null;

  const totalCount = applications.length;
  const isSingleDepartment = groupedDepartments.length <= 1;

  const headerNode = (
    <div className="modal-header">
      <h3 className="modal-title flex items-center gap-2">
        <CalendarDays className={`w-5 h-5 ${theme.icon}`} />
        Leave Applications - {formatDate(date)}
        {totalCount > 0 && (
          <CountBadge
            count={totalCount}
            theme={isAdmin ? 'admin' : 'employee'}
            size="md"
            max={9999}
          />
        )}
      </h3>
      <button onClick={onClose} className={isAdmin ? 'modal-close-admin' : 'modal-close'} aria-label="Close modal">
        <XCircle className="w-5 h-5" />
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      size="lg"
      theme={isAdmin ? 'admin' : 'employee'}
      customHeader={headerNode}
    >
            {totalCount > 0 ? (
              <div className="space-y-3">
                {/* Department Toggle Pills — dept identity colours (DS Department Identity) */}
                {!isSingleDepartment && (
                  <div className="flex flex-wrap gap-1.5 pb-3 border-b border-gray-100">
                    <button
                      type="button"
                      onClick={expandAll}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                        isAdmin ? 'focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'
                      } ${
                        collapsedDepts.size === 0 ? theme.pillActive : theme.pillInactive
                      }`}
                    >
                      All
                      <CountBadge count={totalCount} theme={isAdmin ? 'admin' : 'employee'} active={collapsedDepts.size === 0} />
                    </button>
                    {groupedDepartments.map(({ department, applications: apps }) => {
                      const isExpanded = !collapsedDepts.has(department);
                      const dc = getDepartmentColor(department);
                      const DeptIcon = getDepartmentIcon(department);
                      const shortName = getDepartmentShortName(department);
                      return (
                        <button
                          key={department}
                          type="button"
                          title={department}
                          onClick={() => handlePillClick(department)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${dc.ring} ${
                            isExpanded
                              ? `${dc.solid} text-white border-transparent`
                              : `${dc.bg} ${dc.text} border-transparent hover:opacity-90`
                          }`}
                        >
                          <DeptIcon className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
                          {shortName}
                          <CountBadge count={apps.length} theme="neutral" active={isExpanded} />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Department Sections */}
                {groupedDepartments.map(({ department, applications: apps }) => {
                  const dc = getDepartmentColor(department);
                  const DeptIcon = getDepartmentIcon(department);
                  return (
                  <div key={department} id={`dept-section-${department.replace(/\s+/g, '-')}`}>
                    {!isSingleDepartment && (
                      <button
                        type="button"
                        onClick={() => toggleDept(department)}
                        className="flex items-center gap-2 w-full text-left py-1.5 px-1 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-300"
                      >
                        {collapsedDepts.has(department) ? (
                          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                        <div className={`w-6 h-6 ${dc.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <DeptIcon className={`w-3.5 h-3.5 ${dc.text}`} aria-hidden="true" />
                        </div>
                        <span className="text-sm font-semibold text-gray-700">{department}</span>
                        <CountBadge count={apps.length} theme={isAdmin ? 'admin' : 'employee'} />
                      </button>
                    )}

                    {!collapsedDepts.has(department) && (
                      <div className={`space-y-1.5 ${!isSingleDepartment ? 'ml-6' : ''}`}>
                        {apps.map((app, index) => {
                          const { code: projectCode, name: projectName } = getLeaveProjectFields(app);
                          return (
                            <div key={app.id || index} className="bg-white rounded-xl border border-gray-200 px-4 py-2.5 hover:bg-gray-50 transition-colors">
                              {/* Row 1: Name + Trade | Badge */}
                              <div className="flex justify-between items-center mb-1">
                                <div className="flex items-center gap-2 min-w-0 flex-1 mr-3">
                                  <h4 className="text-sm font-semibold text-gray-900 truncate">{app.employee_name}</h4>
                                  {app.trade_code &&
                                    !isTradeRedundantWithDepartment(department, app.trade_code) && (
                                    <>
                                      <span className="text-xs text-gray-400 flex-shrink-0">·</span>
                                      <TradeBadge code={app.trade_code} size="xs" />
                                    </>
                                  )}
                                </div>
                                {renderBadge && (
                                  <div className="flex-shrink-0">
                                    {renderBadge(app)}
                                  </div>
                                )}
                              </div>

                              {/* Row 2: [LeaveType ·] Date Range · Duration */}
                              <div className="flex items-center gap-1.5 text-xs text-gray-600 flex-wrap">
                                {renderLeaveTypeInline && (
                                  <>
                                    {renderLeaveTypeInline(app)}
                                    <span className="text-gray-300">·</span>
                                  </>
                                )}
                                <LeavePeriodDisplay application={app} showIcon size="xs" className="!text-gray-600 !p-0" />
                                <span className="text-gray-300 mx-0.5">·</span>
                                <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span className="font-medium text-gray-700">
                                  {formatLeaveDays(app.leave_total_days)} {pluralizeDays(app.leave_total_days)}
                                </span>
                              </div>

                              {/* Row 3 (conditional): Reason */}
                              {app.leave_reason && (
                                <p className="text-xs text-gray-500 mt-1 truncate" title={app.leave_reason}>
                                  <span className="font-medium text-gray-600">Reason:</span> {app.leave_reason}
                                </p>
                              )}

                              {/* Row 4 (conditional): Project — name primary, code secondary (DS) */}
                              {projectCode && (
                                <p
                                  className="text-xs text-gray-500 mt-0.5 truncate"
                                  title={projectName ? `${projectName} (${projectCode})` : projectCode}
                                >
                                  <span className="font-medium text-gray-600">Project:</span>{' '}
                                  {projectName ? (
                                    <>
                                      <span className="text-gray-700">{projectName}</span>
                                      {' '}
                                      <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{projectCode}</span>
                                    </>
                                  ) : (
                                    <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">{projectCode}</span>
                                  )}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <CalendarDays className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No leave applications</h3>
                <p className="text-gray-500">No leave applications found for this date.</p>
              </div>
            )}
    </Modal>
  );
};

export default GroupedDateLeaveModal;
